/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Dropdown,
  FormLabel,
  PasswordInput,
  TextInput
} from 'carbon-components-react';
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { ChannelContentProps } from '../../../util/types';
import { ControlledCheckbox } from '../../common/ControlledCheckbox';
import { FIELDS } from '../schema';
import { AdvancedOptions as messages } from './messages';

export const ServiceOrchestration = ({ readOnly }: ChannelContentProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { control } = formMethods;
  const { getFieldValue, registerInputField } = useFormHelpers(formMethods);

  // SIP Auth Enabled
  const soeEnabled = getFieldValue(FIELDS.SOE.ENABLED);

  const versionItems = [
    t(messages.soeSelectOptionOne),
    t(messages.soeSelectOptionTwo)
  ];

  return (
    <>
      <div className="TabContent__instructions-no-spacer">
        <h4>
          <FormattedMessage {...messages.subtitleFour} />
        </h4>
        <div className="TabContent__description" />
        <div className="TabContent__field-container">
          <ControlledCheckbox
            readOnly={readOnly}
            field={FIELDS.SOE.ENABLED}
            labelText={t(messages.enableSoe)}
          />

          {!soeEnabled && (
            <div className="TabContent__description Phone__AdvancedOptions-checkbox-helper">
              <FormLabel>
                <FormattedMessage {...messages.enableSoeHelperText} />
              </FormLabel>
            </div>
          )}
          {soeEnabled && (
            <div className="Phone__AdvancedOptions-checkbox">
              <div className="Phone__AdvancedOptions-checkbox-first">
                <TextInput
                  {...registerInputField({
                    field: FIELDS.SOE.URL,
                    placeholderText: messages.soeUrlPlaceholder,
                    labelText: messages.soeUrlLabel,
                    readOnly
                  })}
                  readOnly={readOnly}
                />
              </div>
              <div className="Phone__AdvancedOptions-checkbox-middle">
                <Controller
                  control={control}
                  name={FIELDS.SOE.MESSAGING_VERSION}
                  render={({ field: { onChange } }) => (
                    <Dropdown
                      id={FIELDS.SOE.MESSAGING_VERSION}
                      titleText={t(messages.soeSelect)}
                      label={t(messages.soeSelectLabel)}
                      defaultValue={t(messages.soeSelectOptionOne)}
                      items={versionItems}
                      onChange={({ selectedItem }) => {
                        onChange(selectedItem);
                      }}
                    />
                  )}
                />
              </div>
              <div className="Phone__AdvancedOptions-checkbox-middle">
                <TextInput
                  {...registerInputField({
                    field: FIELDS.SOE.USERNAME,
                    placeholderText: messages.soeUsernamePlaceholder,
                    labelText: messages.soeUsernameLabel,
                    readOnly
                  })}
                />
              </div>
              <PasswordInput
                {...registerInputField({
                  field: FIELDS.SOE.PASSWORD,
                  placeholderText: messages.soePassword,
                  labelText: messages.soePassword,
                  readOnly
                })}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};
